/* ==========================================================================
   Apus Ami Hub — chế độ toàn màn hình của widget (tương tự "Qonnex Hub").

   Cỡ compact/wide vẫn là khung chat nổi như cũ. Bấm "Toàn màn hình" thì panel
   thành một ứng dụng nhỏ:
     Trò chuyện  cột trái lịch sử (tìm kiếm, ghim, đổi tên, xoá), giữa là chat,
                 cột phải Nguồn / Hoạt động (agent đã làm gì) / Kết quả (biểu đồ, bảng, PDF)
     Duyệt       hộp duyệt đề xuất hành động (người duyệt) + đề xuất của tôi
     Báo cáo     báo cáo đã lưu từ chat, chạy lại, lịch chạy, các phiên bản
     Tổng quan   số liệu sử dụng AI (chỉ vai có quyền duyệt)
   + chuông thông báo.

   Chỉ chạy khi có server (apiBase): mọi dữ liệu nằm ở server/hub.mjs, theo
   danh tính từ token — widget không giữ gì ngoài cache hiển thị.
   Mọi chữ từ server/mô hình đi qua textContent hoặc renderMarkdown (đã escape).
   ========================================================================== */
import type { ActionProposal, AnswerSource, ChatMessage, OpenContext, PanelSize } from "../types.ts";
import { el, Listeners } from "../ui/dom.ts";
import { icon } from "../ui/icons.ts";
import { renderMarkdown } from "../core/markdown.ts";
import { normalizeProposals } from "../core/transport.ts";
import { renderProposal, stateLabel } from "../ui/proposal.ts";
import type { View } from "../ui/view.ts";
import { channelErrorText, createLinkCode, loadChannelRows, needsLinkReminder, renderChannels, unlinkChannel, type ChannelRow, type ChatChannel, type LinkCode } from "./channel-links.ts";
import { disconnectErpLink, loadErpLinks, needsAttention, reconnectTarget, renderErpLinks, startErpLink, whenClosed, type ErpLink, type HubApi, type PopupLike } from "./erp-links.ts";

type Api = <T = Record<string, unknown>>(method: "GET" | "POST" | "PATCH" | "DELETE", path: string, body?: unknown) => Promise<T>;

export interface HubHost {
  api: Api;
  view: View;
  messages(): ChatMessage[];
  conversationId(): string | null;
  openConversation(id: string, focusIndex?: number): Promise<void>;
  newConversation(): void;
  setSize(size: PanelSize): void;
  close(): void;
  send(text: string): void;
  context(): OpenContext | null;
  openRoleMenu(anchor: HTMLElement): void;
  download(url: string, name?: string): Promise<void>;
  /** Xem trước PDF trong khung chat (có nút Tải về trong khung). */
  previewPdf(url: string, name: string): Promise<void>;
  /** Bản demo: server bật nút "Xoá dữ liệu demo" (cạnh chuông, chỉ ở chế độ toàn màn hình). */
  canDemoReset(): boolean;
  demoReset(): void;
}

type Tab = "chat" | "tasks" | "approvals" | "reports" | "overview" | "admin";
type AsideTab = "sources" | "activity" | "results" | "context";
type ApprovalFilter = "pending" | "mine" | "decided";

interface Me {
  name: string;
  role: string;
  roleLabel: string;
  title?: string;
  canApprove: boolean;
  tabs: Tab[];
  counts: { approvals: number; unread: number; tasks?: number };
  can?: string[];
  snapshot?: string | null;
  memory?: { enabled: boolean; items: Array<{ id: string; text: string; at: number; source: string }> };
}
interface Task {
  id: string;
  title: string;
  goal: string;
  status: "running" | "needs_input" | "done" | "failed" | "cancelled";
  createdAt: number;
  updatedAt: number;
  steps: Array<{ at: number; t: number; label: string }>;
  result: string;
  question?: string | null;
  error?: string;
  sources?: AnswerSource[];
  proposals: unknown[];
  runs: Array<{ at: number; ms: number; status: string }>;
}
const TASK_STATE: Record<Task["status"], [string, string]> = {
  running: ["Đang chạy", "info"],
  needs_input: ["Cần bạn quyết định", "warn"],
  done: ["Xong", "ok"],
  failed: ["Lỗi", "bad"],
  cancelled: ["Đã huỷ", "muted"],
};
const TASK_IDEAS = [
  "Rà 10 PI tháng 10, liệt kê điểm cần chốt với từng khách và soạn email nhắc ngắn",
  "Tổng hợp tình hình đơn 01 từ lúc đặt hàng đến giao hàng, liệt kê việc còn mở và người phụ trách",
  "Kiểm tra MRP đơn 01; vật tư nào còn thiếu thì lập đề nghị mua",
  "So sánh giá thành 10 đơn xuất khẩu, chỉ ra 3 đơn lợi nhuận thấp nhất và lý do",
  "Tổng hợp kết quả QC tháng 10, đề xuất lô cần giữ lại kiểm tra thêm",
];
interface ConvItem {
  id: string;
  title: string;
  pinned: boolean;
  updatedAt: number;
  count: number;
}
interface SearchHit {
  conversationId: string;
  title: string;
  index: number;
  role: string;
  at: number;
  snippet: string;
}
interface Notif {
  id: string;
  kind: string;
  title: string;
  body?: string;
  at: number;
  read: boolean;
  ref?: { tab?: Tab; id?: string; handoff?: string };
}
interface Run {
  id: string;
  at: number;
  status: "running" | "ok" | "error";
  ms: number;
  by?: string;
  text?: string;
  sources?: AnswerSource[];
  trace?: Array<{ t: number; label: string }>;
  error?: string;
}
interface Job {
  id: string;
  title: string;
  prompt: string;
  schedule: { kind: string; time?: string; dow?: number };
  scheduleLabel: string;
  paused: boolean;
  nextAt: number | null;
  lastRun: { id: string; at: number; status: string; ms: number } | null;
  runCount: number;
  runs?: Run[];
  deliver?: { to: string[] };
}

const TAB_LABEL: Record<Tab, string> = { chat: "Trò chuyện", tasks: "Giao việc", approvals: "Duyệt", reports: "Báo cáo", overview: "Tổng quan", admin: "Quản trị" };

/* ---------------------------------------------------------- tiện ích */
const txt = <K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, value: string): HTMLElementTagNameMap[K] => {
  const n = el(tag, cls);
  n.textContent = value;
  return n;
};
const btn = (cls: string, label: string, ico?: string, attrs: Record<string, string> = {}): HTMLButtonElement => {
  const b = el("button", cls, { type: "button", ...attrs });
  if (ico) b.innerHTML = icon(ico, 14);
  if (label) b.append(document.createTextNode((ico ? " " : "") + label));
  return b;
};
/* Giờ Việt Nam (UTC+7, không giờ mùa hè), tự định dạng: Intl "vi-VN" khác nhau giữa các bản ICU
   (có nơi ra "01-10" thay vì "01/10"). */
const VN_MS = 7 * 3600e3;
const pad = (n: number) => String(n).padStart(2, "0");
const vn = (at: number) => new Date(at + VN_MS);
const dayMonth = (at: number) => pad(vn(at).getUTCDate()) + "/" + pad(vn(at).getUTCMonth() + 1);
const vnDay = (at: number) => vn(at).toISOString().slice(0, 10);
export function ago(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return "vừa xong";
  if (s < 3600) return Math.round(s / 60) + " phút trước";
  if (s < 86400) return Math.round(s / 3600) + " giờ trước";
  return dayMonth(at);
}
const clock = (at: number) => pad(vn(at).getUTCHours()) + ":" + pad(vn(at).getUTCMinutes()) + " " + dayMonth(at);

/** Nhóm hội thoại theo thời gian như Qonnex Hub: Đã ghim / Hôm nay / Hôm qua / 7 ngày qua / Trước đó. */
export function groupConversations(list: ConvItem[], now = Date.now()): Array<[string, ConvItem[]]> {
  const day = vnDay;
  const today = day(now);
  const yesterday = day(now - 86400e3);
  const groups: Record<string, ConvItem[]> = { "Đã ghim": [], "Hôm nay": [], "Hôm qua": [], "7 ngày qua": [], "Trước đó": [] };
  for (const c of list) {
    const key = c.pinned ? "Đã ghim" : day(c.updatedAt) === today ? "Hôm nay" : day(c.updatedAt) === yesterday ? "Hôm qua" : now - c.updatedAt < 7 * 86400e3 ? "7 ngày qua" : "Trước đó";
    (groups[key] as ConvItem[]).push(c);
  }
  return Object.entries(groups).filter(([, v]) => v.length > 0);
}

/** Những gì agent sinh ra trong hội thoại: biểu đồ, bản xem trước HTML, bảng, file PDF. */
export function artifactsOf(messages: ChatMessage[]): Array<{ messageId: string; kind: "chart" | "html" | "table" | "pdf"; title: string; url?: string }> {
  const out: Array<{ messageId: string; kind: "chart" | "html" | "table" | "pdf"; title: string; url?: string }> = [];
  for (const m of messages) {
    if (m.role !== "assistant") continue;
    const re = /```(chart|html|htm|svg)\n([\s\S]*?)```/g;
    for (let r = re.exec(m.text); r; r = re.exec(m.text)) {
      if (r[1] === "chart") {
        let title = "Biểu đồ";
        try {
          title = String((JSON.parse(String(r[2]).trim()) as { title?: string }).title || title);
        } catch {
          /* khối hỏng: vẫn liệt kê */
        }
        out.push({ messageId: m.id, kind: "chart", title });
      } else {
        const t = /<h[1-3][^>]*>([^<]{2,80})</i.exec(String(r[2])) || /<title>([^<]{2,80})</i.exec(String(r[2]));
        out.push({ messageId: m.id, kind: "html", title: t ? String(t[1]).trim() : r[1] === "svg" ? "Hình SVG" : "Bản xem trước HTML" });
      }
    }
    const table = /^\|(.+)\|\s*\n\|[\s:|-]+\|/m.exec(m.text.replace(/```[\s\S]*?```/g, ""));
    if (table) out.push({ messageId: m.id, kind: "table", title: "Bảng: " + String(table[1]).split("|").map((x) => x.trim()).filter(Boolean).slice(0, 3).join(" · ") });
    for (const a of m.actions || []) if (a.type === "link" && /\.pdf(\?|$)/.test(a.target)) out.push({ messageId: m.id, kind: "pdf", title: a.label, url: a.target });
  }
  return out;
}

export class Hub {
  private host: HubHost;
  private L = new Listeners();
  private active = false;
  private tab: Tab = "chat";
  private me: Me | null = null;
  private convs: ConvItem[] = [];
  private query = "";
  private hits: SearchHit[] | null = null;
  private asideTab: AsideTab = "sources";
  private notifs: Notif[] = [];
  private bellOpen = false;
  /* Story 13.6 — kết nối tài khoản ERP (chỉ Java; Node demo ⇒ rỗng ⇒ ẩn) */
  private erpLinks: ErpLink[] = [];
  private erpOpen = false;
  private erpBusy: string | null = null;
  private erpNote: string | null = null;
  private erpWait: (() => void) | null = null;
  /* Story 12.10/13.13 — "Kênh nhận thông báo" (chỉ Java; Node demo / môi trường chưa nối NTF ⇒ rỗng ⇒ ẩn) */
  private chnRows: ChannelRow[] = [];
  private chnLoaded = false;
  private chnOpen = false;
  private chnBusy: ChatChannel | null = null;
  private chnNote: string | null = null;
  private chnCodes: Partial<Record<ChatChannel, LinkCode>> = {};
  private readonly erpApi: HubApi = <T,>(m: string, p: string, body?: unknown) => this.host.api<T>(m as "GET", p, body);
  private poll: ReturnType<typeof setInterval> | null = null;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private jobPoll: ReturnType<typeof setTimeout> | null = null;
  private selected: { approvals: string | null; reports: string | null; run: string | null; tasks: string | null } = { approvals: null, reports: null, run: null, tasks: null };
  private overviewRole = "";
  private adminDays = 30;
  private taskPoll: ReturnType<typeof setTimeout> | null = null;
  private approvalsFilter: ApprovalFilter = "pending";

  /* các vùng DOM */
  private bar: HTMLDivElement;
  private side: HTMLElement;
  private aside: HTMLElement;
  private view: HTMLDivElement;
  private convhead: HTMLDivElement;
  private toastEl: HTMLDivElement;

  constructor(host: HubHost) {
    this.host = host;
    const r = host.view.refs;
    this.bar = r.hubbar;
    this.side = r.side;
    this.aside = r.aside;
    this.view = r.hubview;
    this.convhead = el("div", "apx-convhead", { hidden: "" });
    r.main.insertBefore(this.convhead, r.main.firstChild);
    this.toastEl = el("div", "apx-toast", { role: "status", hidden: "" });
    r.panel.appendChild(this.toastEl);
    this.L.add(this.bar, "click", (e) => this.onBarClick(e));
    this.L.add(this.side, "click", (e) => void this.onSideClick(e));
    this.L.add(this.side, "input", (e) => this.onSearch(e));
    this.L.add(this.aside, "click", (e) => this.onAsideClick(e));
    this.L.add(this.view, "click", (e) => void this.onViewClick(e));
    this.L.add(this.view, "change", (e) => void this.onViewChange(e));
    this.L.add(this.convhead, "click", (e) => void this.onConvheadClick(e));
  }

  /* ------------------------------------------------------- vòng đời */
  activate(): void {
    if (this.active) return;
    this.active = true;
    this.host.view.refs.root.dataset.hub = "on";
    this.bar.hidden = false;
    this.showTab(this.tab);
    void this.refreshMe();
    void this.loadConversations();
    this.poll = setInterval(() => void this.refreshMe(), 20000);
  }

  deactivate(): void {
    if (!this.active) return;
    this.active = false;
    delete this.host.view.refs.root.dataset.hub;
    for (const n of [this.bar, this.side, this.aside, this.view, this.convhead]) n.hidden = true;
    this.host.view.refs.main.hidden = false;
    if (this.poll) clearInterval(this.poll);
    this.poll = null;
    this.bellOpen = false;
  }

  isActive(): boolean {
    return this.active;
  }

  destroy(): void {
    this.deactivate();
    this.L.clear();
    this.erpWait?.();
    if (this.jobPoll) clearTimeout(this.jobPoll);
    if (this.taskPoll) clearTimeout(this.taskPoll);
    this.convhead.remove();
    this.toastEl.remove();
  }

  /** Sau mỗi câu trả lời / đổi hội thoại: làm mới danh sách và cột phải. */
  onMessagesChanged(): void {
    if (!this.active) return;
    this.renderAside();
    this.renderConvhead();
    void this.loadConversations();
    void this.refreshMe();
  }

  /** Đổi vai (demo): tải lại danh tính, quyền, danh sách — về tab Trò chuyện. */
  onRoleChanged(): void {
    this.convs = [];
    this.notifs = [];
    this.chnRows = [];
    this.chnLoaded = false;
    this.chnOpen = false;
    this.chnCodes = {};
    this.chnNote = null;
    this.selected = { approvals: null, reports: null, run: null, tasks: null };
    this.query = "";
    this.hits = null;
    if (!this.active) return;
    this.tab = "chat";
    void this.refreshMe().then(() => this.showTab("chat"));
    void this.loadConversations();
  }

  toast(message: string): void {
    this.toastEl.textContent = message;
    this.toastEl.hidden = false;
    setTimeout(() => (this.toastEl.hidden = true), 2600);
  }

  /** Mở thẳng một tab (ví dụ từ thông báo). */
  go(tab: Tab, id?: string): void {
    if (tab === "approvals" && id) this.selected.approvals = id;
    if (tab === "tasks" && id) this.selected.tasks = id;
    if (tab === "reports" && id) {
      this.selected.reports = id;
      this.selected.run = null;
    }
    this.showTab(tab);
  }

  /* ------------------------------------------------------ thanh trên */
  private async refreshMe(): Promise<void> {
    try {
      this.me = await this.host.api<Me>("GET", "/me");
    } catch {
      return;
    }
    this.erpLinks = await loadErpLinks(this.erpApi);
    /* kênh: tải lần đầu và khi mục đang mở (đi qua NTF — ⛔ mỗi nhịp 20 s) */
    if (!this.chnLoaded || this.chnOpen) await this.loadChannels();
    this.renderBar();
  }

  private async loadChannels(): Promise<void> {
    this.chnRows = await loadChannelRows(this.erpApi);
    this.chnLoaded = true;
    for (const r of this.chnRows) if (r.linked) delete this.chnCodes[r.channel];
    if (!this.chnRows.length) this.chnOpen = false;
  }

  private renderBar(): void {
    const me = this.me;
    this.bar.replaceChildren();
    const brand = el("div", "apx-hub-brand");
    const logo = el("span", "apx-hub-logo");
    const mascot = this.host.view.mascotUrl();
    if (mascot) {
      const img = this.host.view.mascotFace("apx-hub-logo-img") || el("img", "apx-hub-logo-img", { src: mascot, alt: "" });
      logo.classList.add("apx-has-mascot");
      logo.append(img);
    } else logo.innerHTML = icon("spark", 16);
    brand.append(logo, txt("span", "apx-hub-name", "Apus Ami"));
    const nav = el("nav", "apx-hub-tabs", { role: "tablist" });
    for (const t of me?.tabs || ["chat"]) {
      const label = t === "approvals" && me && !me.canApprove ? "Đề xuất" : TAB_LABEL[t];
      const b = btn("apx-hub-tab" + (t === this.tab ? " apx-on" : ""), label, undefined, { "data-tab": t, role: "tab", "aria-selected": String(t === this.tab) });
      if (t === "approvals" && me && me.counts.approvals > 0) b.append(txt("span", "apx-count", String(me.counts.approvals)));
      if (t === "tasks" && me && (me.counts.tasks || 0) > 0) b.append(txt("span", "apx-count", String(me.counts.tasks)));
      nav.append(b);
    }
    const spacer = el("div", "apx-hub-spacer");
    const bell = btn("apx-hub-icon apx-bell", "", "bell", { "data-bar": "bell", title: "Thông báo", "aria-label": "Thông báo", "aria-expanded": String(this.bellOpen) });
    if (me && me.counts.unread > 0) bell.append(txt("span", "apx-dot", String(Math.min(99, me.counts.unread))));
    /* góc trên bên phải: tên + chức vụ; bản demo bấm để đổi vai */
    const canSwitch = this.host.view.hasDemoRoles();
    const who = canSwitch
      ? el("button", "apx-hub-who apx-hub-whobtn", { type: "button", "data-bar": "who", "aria-haspopup": "menu", title: "Đổi vai (bản demo)" })
      : el("div", "apx-hub-who");
    const words = (me?.name || "?").trim().split(/\s+/);
    who.append(txt("span", "apx-hub-avatar", (words.length > 1 ? (words[words.length - 2] || "").charAt(0) + (words[words.length - 1] || "").charAt(0) : (words[0] || "?").charAt(0)).toUpperCase()));
    const whoText = el("div", "apx-hub-whotext");
    whoText.append(txt("div", "apx-hub-whoname", me?.name || "…"), txt("div", "apx-hub-whorole", me?.title || me?.roleLabel || ""));
    who.append(whoText);
    if (canSwitch) who.append(txt("span", "apx-caret", "▾"));
    const narrow = btn("apx-hub-icon", "", "narrow", { "data-bar": "narrow", title: "Thu về khung chat", "aria-label": "Thu về khung chat" });
    const close = btn("apx-hub-icon", "", "close", { "data-bar": "close", title: "Đóng", "aria-label": "Đóng" });
    /* bản demo: nhân viên Apus tự xoá dữ liệu hỏi đáp thử (mật khẩu) — cạnh chuông */
    const extra = this.host.canDemoReset() ? [btn("apx-hub-icon apx-demoreset", "", "trash", { "data-bar": "demoreset", title: "Xoá dữ liệu demo", "aria-label": "Xoá dữ liệu demo" })] : [];
    /* Story 13.6: "Tài khoản ERP" — chỉ khi tenant có ERP hồ sơ user-oauth-pkce; chấm = số ERP chưa/hết kết nối */
    const erp: HTMLElement[] = [];
    if (this.erpLinks.length) {
      const b = btn("apx-hub-icon apx-erp", "", "link", { "data-bar": "erp", title: "Tài khoản ERP", "aria-label": "Tài khoản ERP", "aria-expanded": String(this.erpOpen) });
      const off = needsAttention(this.erpLinks);
      if (off) b.append(txt("span", "apx-dot", String(off)));
      erp.push(b);
    }
    /* Story 12.10/13.13: "Kênh nhận thông báo" — chấm = số kênh tôi CHỌN mà chưa liên kết (NTF bỏ qua tin tới kênh ấy) */
    if (this.chnRows.length) {
      const b = btn("apx-hub-icon apx-chn", "", "chat", { "data-bar": "channels", title: "Kênh nhận thông báo", "aria-label": "Kênh nhận thông báo", "aria-expanded": String(this.chnOpen) });
      const off = needsLinkReminder(this.chnRows);
      if (off) b.append(txt("span", "apx-dot", String(off)));
      erp.push(b);
    }
    this.bar.append(brand, nav, spacer, ...extra, ...erp, bell, who, narrow, close);
    if (this.bellOpen) this.bar.append(this.renderBell());
    if (this.erpOpen && this.erpLinks.length) this.bar.append(renderErpLinks(this.erpLinks, this.erpBusy, this.erpNote));
    if (this.chnOpen && this.chnRows.length) this.bar.append(renderChannels(this.chnRows, { busy: this.chnBusy, note: this.chnNote, codes: this.chnCodes }));
  }

  /** Story 13.13 AC2 — "Liên kết" ⇒ mã một lần + hướng dẫn; "Gỡ" ⇒ DELETE rồi tải lại. Lỗi ⇒ ghi chú trong mục, ⛔ vỡ Hub. */
  private onChnAct(t: HTMLElement): boolean {
    const b = t.closest("[data-chn-act]") as HTMLElement | null;
    const ch = b?.getAttribute("data-chn") as ChatChannel | null | undefined;
    const act = b?.getAttribute("data-chn-act");
    if (!b || !ch || !act || this.chnBusy) return !!b;
    this.chnBusy = ch;
    this.chnNote = null;
    this.renderBar();
    const done = async () => {
      this.chnBusy = null;
      if (act === "unlink") await this.loadChannels();
      this.renderBar();
    };
    if (act === "unlink") {
      void unlinkChannel(this.erpApi, ch).catch((err) => (this.chnNote = channelErrorText(err, "Chưa gỡ được"))).then(done);
    } else {
      void createLinkCode(this.erpApi, ch)
        .then((c) => (this.chnCodes[ch] = c))
        .catch((err) => {
          delete this.chnCodes[ch];
          this.chnNote = channelErrorText(err, "Chưa tạo được mã");
        })
        .then(done);
    }
    return true;
  }

  private renderBell(): HTMLElement {
    const pop = el("div", "apx-bellpop", { role: "dialog", "aria-label": "Thông báo" });
    const head = el("div", "apx-bellhead");
    head.append(txt("strong", "", "Thông báo"), btn("apx-link", "Đánh dấu đã đọc", undefined, { "data-bar": "readall" }));
    pop.append(head);
    if (!this.notifs.length) pop.append(txt("p", "apx-empty", "Chưa có thông báo. Đề xuất cần duyệt, kết quả duyệt và báo cáo chạy xong sẽ hiện ở đây."));
    const ICON: Record<string, string> = { approval: "shield", done: "check", rejected: "close", info: "info", job: "chart", job_failed: "warn", handoff: "arrow" };
    for (const n of this.notifs) {
      const row = el("button", "apx-notif" + (n.read ? "" : " apx-unread"), { type: "button", "data-notif": n.id });
      const ic = el("span", "apx-notif-ico apx-k-" + n.kind);
      ic.innerHTML = icon(ICON[n.kind] || "bell", 14);
      const body = el("span", "apx-notif-body");
      body.append(txt("span", "apx-notif-title", n.title));
      if (n.body) body.append(txt("span", "apx-notif-text", n.body));
      body.append(txt("span", "apx-notif-time", ago(n.at)));
      row.append(ic, body);
      pop.append(row);
    }
    return pop;
  }

  /** Việc chuyển tiếp từ phòng khác: mở hội thoại MỚI với câu hỏi điền sẵn (hoặc Tổng quan cho Giám đốc). */
  private async openHandoff(hid: string): Promise<void> {
    try {
      const r = await this.host.api<{ handoff: { tab: Tab; prompt: string } }>("POST", "/handoffs/" + encodeURIComponent(hid) + "/open", {});
      if (r.handoff.tab === "overview") return this.showTab("overview");
      this.host.newConversation();
      this.showTab("chat");
      if (r.handoff.prompt) this.host.send(r.handoff.prompt);
    } catch {
      this.renderBar();
    }
  }

  /**
   * Story 13.5/13.6 — nút "Kết nối" / "Kết nối lại" / "Ngắt kết nối" (thanh trên + thẻ đề xuất need_info). Cửa sổ bật mở ĐỒNG BỘ trong cú
   * bấm (chặn popup) rồi mới xin URL; đóng cửa sổ ⇒ tải lại trạng thái. Trả true khi đã xử lý.
   */
  private onErpAct(t: HTMLElement): boolean {
    const b = t.closest("[data-erp-act]") as HTMLElement | null;
    const id = b?.getAttribute("data-erp-id");
    const act = b?.getAttribute("data-erp-act");
    if (!b || !id || !act || this.erpBusy) return !!b;
    const api = this.erpApi;
    const refresh = async () => {
      this.erpBusy = null;
      this.erpLinks = await loadErpLinks(api);
      this.renderBar();
      if (this.tab === "approvals") void this.renderApprovals();
    };
    this.erpNote = null;
    if (act === "disconnect") {
      this.erpBusy = id;
      this.renderBar();
      void disconnectErpLink(api, id).catch((err: Error) => (this.erpNote = "Ngắt chưa được: " + err.message)).then(refresh);
      return true;
    }
    const popup = (typeof window !== "undefined" ? window.open("about:blank", "apx-erp-link", "popup,width=520,height=680") : null) as PopupLike | null;
    this.erpBusy = id;
    this.erpOpen = true;
    this.renderBar();
    void startErpLink(api, id, popup)
      .then((url) => {
        if (!popup) {
          this.erpNote = "Trình duyệt chặn cửa sổ bật — cho phép cửa sổ bật rồi bấm lại (" + new URL(url).host + ").";
          return refresh();
        }
        this.erpWait?.();
        this.erpWait = whenClosed(popup, () => {
          this.erpWait = null;
          void refresh();
        });
      })
      .catch((err: Error) => {
        this.erpNote = "Chưa mở được trang đăng nhập ERP: " + err.message;
        return refresh();
      });
    return true;
  }

  private onBarClick(e: Event): void {
    const t = e.target as HTMLElement;
    if (this.onErpAct(t)) return;
    if (this.onChnAct(t)) return;
    const tab = (t.closest("[data-tab]") as HTMLElement | null)?.dataset.tab as Tab | undefined;
    if (tab) return this.showTab(tab);
    const notif = (t.closest("[data-notif]") as HTMLElement | null)?.dataset.notif;
    if (notif) {
      const n = this.notifs.find((x) => x.id === notif);
      void this.host.api("POST", "/notifications/read", { ids: [notif] }).then(() => this.refreshMe());
      this.bellOpen = false;
      if (n?.ref?.handoff) void this.openHandoff(n.ref.handoff);
      else if (n?.ref?.tab) this.go(n.ref.tab, n.ref.id);
      else this.renderBar();
      return;
    }
    const act = (t.closest("[data-bar]") as HTMLElement | null)?.dataset.bar;
    if (act === "who") return this.host.openRoleMenu(t.closest("[data-bar='who']") as HTMLElement);
    if (act === "narrow") this.host.setSize("wide");
    else if (act === "close") this.host.close();
    else if (act === "demoreset") this.host.demoReset();
    else if (act === "erp") {
      this.erpOpen = !this.erpOpen;
      if (this.erpOpen) this.bellOpen = this.chnOpen = false;
      this.renderBar();
    } else if (act === "channels") {
      this.chnOpen = !this.chnOpen;
      if (this.chnOpen) {
        this.bellOpen = this.erpOpen = false;
        void this.loadChannels().then(() => this.renderBar());
      }
      this.renderBar();
    } else if (act === "bell") {
      this.bellOpen = !this.bellOpen;
      if (this.bellOpen) this.erpOpen = this.chnOpen = false;
      if (this.bellOpen) void this.loadNotifs();
      this.renderBar();
    } else if (act === "readall") {
      void this.host.api("POST", "/notifications/read", {}).then(() => {
        for (const n of this.notifs) n.read = true;
        return this.refreshMe();
      });
    }
  }

  private async loadNotifs(): Promise<void> {
    try {
      const r = await this.host.api<{ notifications: Notif[] }>("GET", "/notifications");
      this.notifs = r.notifications || [];
    } catch {
      this.notifs = [];
    }
    this.renderBar();
  }

  /* ------------------------------------------------------------ tab */
  private showTab(tab: Tab): void {
    if (this.me && !this.me.tabs.includes(tab)) tab = "chat";
    this.tab = tab;
    this.bellOpen = false;
    const chat = tab === "chat";
    this.side.hidden = !chat;
    this.aside.hidden = !chat;
    this.convhead.hidden = !chat;
    this.host.view.refs.main.hidden = !chat;
    this.view.hidden = chat;
    this.renderBar();
    if (chat) {
      this.renderSide();
      this.renderAside();
      this.renderConvhead();
    } else if (tab === "tasks") void this.renderTasks();
    else if (tab === "approvals") void this.renderApprovals();
    else if (tab === "reports") void this.renderReports();
    else if (tab === "overview") void this.renderOverview();
    else if (tab === "admin") void this.renderAdmin();
  }

  /* ------------------------------------------------- cột trái: lịch sử */
  private async loadConversations(): Promise<void> {
    try {
      const r = await this.host.api<{ conversations: ConvItem[] }>("GET", "/conversations");
      this.convs = r.conversations || [];
    } catch {
      /* giữ danh sách cũ */
    }
    if (this.tab === "chat") {
      this.renderSide();
      this.renderConvhead();
    }
  }

  private renderSide(): void {
    const keepFocus = this.side.contains(this.host.view.shadow.activeElement);
    this.side.replaceChildren();
    const newBtn = btn("apx-btn apx-btn-primary apx-new", "Cuộc trò chuyện mới", "plus", { "data-side": "new" });
    const search = el("input", "apx-search", { type: "search", placeholder: "Tìm trong hội thoại…", "aria-label": "Tìm trong hội thoại", value: this.query });
    const searchWrap = el("label", "apx-searchwrap");
    const si = el("span", "apx-searchico");
    si.innerHTML = icon("search", 14);
    searchWrap.append(si, search);
    this.side.append(newBtn, searchWrap);

    const list = el("div", "apx-convlist");
    if (this.hits) {
      list.append(txt("div", "apx-group", this.hits.length ? this.hits.length + " kết quả" : "Không tìm thấy"));
      for (const h of this.hits) {
        const row = el("button", "apx-hit", { type: "button", "data-open": h.conversationId, "data-index": String(h.index) });
        row.append(txt("span", "apx-conv-title", h.title), txt("span", "apx-hit-snippet", (h.role === "user" ? "Bạn: " : "AI: ") + h.snippet), txt("span", "apx-conv-meta", ago(h.at)));
        list.append(row);
      }
    } else {
      const current = this.host.conversationId();
      if (!this.convs.length) list.append(txt("p", "apx-empty", "Chưa có hội thoại. Câu hỏi đầu tiên sẽ tạo hội thoại mới và lưu ở đây."));
      for (const [label, items] of groupConversations(this.convs)) {
        list.append(txt("div", "apx-group", label));
        for (const c of items) {
          const row = el("div", "apx-conv" + (c.id === current ? " apx-on" : ""), { "data-conv": c.id });
          const open = el("button", "apx-conv-open", { type: "button", "data-open": c.id, title: c.title });
          open.append(txt("span", "apx-conv-title", c.title), txt("span", "apx-conv-meta", ago(c.updatedAt) + " · " + c.count + " tin"));
          const tools = el("span", "apx-conv-tools");
          tools.append(
            btn("apx-mini" + (c.pinned ? " apx-on" : ""), "", "pin", { "data-pin": c.id, title: c.pinned ? "Bỏ ghim" : "Ghim", "aria-label": c.pinned ? "Bỏ ghim" : "Ghim" }),
            btn("apx-mini", "", "edit", { "data-rename": c.id, title: "Đổi tên", "aria-label": "Đổi tên" }),
            btn("apx-mini", "", "trash", { "data-del": c.id, title: "Xoá", "aria-label": "Xoá" }),
          );
          row.append(open, tools);
          list.append(row);
        }
      }
    }
    this.side.append(list);
    if (keepFocus) {
      search.focus();
      search.setSelectionRange(search.value.length, search.value.length);
    }
  }

  private onSearch(e: Event): void {
    const input = e.target as HTMLInputElement;
    if (!input.classList.contains("apx-search")) return;
    this.query = input.value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(async () => {
      const q = this.query.trim();
      if (q.length < 2) {
        this.hits = null;
      } else {
        try {
          this.hits = (await this.host.api<{ results: SearchHit[] }>("GET", "/conversations?q=" + encodeURIComponent(q))).results || [];
        } catch {
          this.hits = [];
        }
      }
      this.renderSide();
    }, 300);
  }

  private async onSideClick(e: Event): Promise<void> {
    const t = e.target as HTMLElement;
    const get = (k: string) => (t.closest("[data-" + k + "]") as HTMLElement | null)?.getAttribute("data-" + k) || null;
    if (get("side") === "new") {
      this.host.newConversation();
      this.query = "";
      this.hits = null;
      this.renderSide();
      this.renderAside();
      this.renderConvhead();
      return;
    }
    const pin = get("pin");
    if (pin) {
      const c = this.convs.find((x) => x.id === pin);
      await this.host.api("PATCH", "/conversations/" + pin, { pinned: !c?.pinned });
      return void this.loadConversations();
    }
    const ren = get("rename");
    if (ren) return this.inlineRename(ren);
    const del = get("del");
    if (del) {
      const row = this.side.querySelector('[data-conv="' + del + '"]');
      if (row && !row.classList.contains("apx-confirm")) {
        row.classList.add("apx-confirm");
        const ask = el("div", "apx-ask");
        ask.append(txt("span", "", "Xoá hội thoại này?"), btn("apx-link apx-danger", "Xoá", undefined, { "data-del-yes": del }), btn("apx-link", "Thôi", undefined, { "data-del-no": del }));
        row.append(ask);
      }
      return;
    }
    const yes = get("del-yes");
    if (yes) {
      await this.host.api("DELETE", "/conversations/" + yes);
      if (this.host.conversationId() === yes) this.host.newConversation();
      this.toast("Đã xoá hội thoại");
      return void this.loadConversations();
    }
    if (get("del-no")) return this.renderSide();
    const open = get("open");
    if (open) {
      const idx = get("index");
      await this.host.openConversation(open, idx != null ? Number(idx) : undefined);
      this.renderSide();
    }
  }

  private inlineRename(cid: string): void {
    const row = this.side.querySelector('[data-conv="' + cid + '"]');
    const c = this.convs.find((x) => x.id === cid);
    if (!row || !c) return;
    const input = el("input", "apx-rename", { type: "text", value: c.title, "aria-label": "Tên hội thoại", maxlength: "120" });
    row.replaceChildren(input);
    input.focus();
    input.select();
    let done = false;
    const commit = async (save: boolean) => {
      if (done) return;
      done = true;
      const title = input.value.trim();
      if (save && title && title !== c.title) {
        await this.host.api("PATCH", "/conversations/" + cid, { title });
        this.renderConvhead();
      }
      void this.loadConversations();
    };
    input.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") void commit(true);
      if (ev.key === "Escape") {
        ev.stopPropagation();
        void commit(false);
      }
    });
    input.addEventListener("blur", () => void commit(true));
  }

  /* --------------------------------------- đầu khung chat: tên + xuất */
  private renderConvhead(): void {
    const cid = this.host.conversationId();
    const c = this.convs.find((x) => x.id === cid);
    this.convhead.replaceChildren();
    this.convhead.append(txt("div", "apx-convhead-title", c ? c.title : "Cuộc trò chuyện mới"));
    if (c) {
      this.convhead.append(
        btn("apx-btn apx-btn-ghost apx-sm", "Chia sẻ", "share", { "data-share": "open", title: "Tạo link chỉ đọc có hạn" }),
        btn("apx-btn apx-btn-ghost apx-sm", "PDF", "download", { "data-export": "pdf", title: "Xuất hội thoại ra PDF" }),
        btn("apx-btn apx-btn-ghost apx-sm", "Markdown", "file", { "data-export": "md", title: "Xuất hội thoại ra Markdown" }),
      );
    }
  }

  private async onConvheadClick(e: Event): Promise<void> {
    const t = e.target as HTMLElement;
    const cid0 = this.host.conversationId();
    const sh = (t.closest("[data-share]") as HTMLElement | null)?.dataset.share;
    if (sh && cid0) return this.onShare(sh, cid0, (t.closest("[data-sid]") as HTMLElement | null)?.dataset.sid);
    const fmt = (t.closest("[data-export]") as HTMLElement | null)?.dataset.export;
    const cid = this.host.conversationId();
    if (!fmt || !cid) return;
    try {
      const r = await this.host.api<{ url?: string; filename?: string; content?: string }>("POST", "/conversations/" + cid + "/export", { format: fmt });
      if (r.url) await this.host.previewPdf(r.url, "Hội thoại Apus Ami.pdf");
      else if (r.content) {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([r.content], { type: "text/markdown;charset=utf-8" }));
        a.download = r.filename || "apus-ami.md";
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      }
    } catch (err) {
      this.toast("Không xuất được: " + (err as Error).message);
    }
  }

  /* ------------------------------------------- cột phải: chi tiết */
  private renderAside(): void {
    if (!this.active || this.tab !== "chat") return;
    const msgs = this.host.messages();
    const last = [...msgs].reverse().find((m) => m.role === "assistant" && !m.streaming);
    this.aside.replaceChildren();
    const tabs = el("div", "apx-aside-tabs", { role: "tablist" });
    const items: Array<[AsideTab, string]> = [["sources", "Nguồn"], ["activity", "Hoạt động"], ["results", "Kết quả"], ["context", "Ngữ cảnh"]];
    for (const [k, label] of items) tabs.append(btn("apx-aside-tab" + (k === this.asideTab ? " apx-on" : ""), label, undefined, { "data-aside": k, role: "tab", "aria-selected": String(k === this.asideTab) }));
    const body = el("div", "apx-aside-body");
    this.aside.append(tabs, body);

    if (this.asideTab === "sources") {
      body.append(txt("p", "apx-aside-hint", "Nguồn của câu trả lời gần nhất — đối chiếu từng con số."));
      const src = (last?.sources || []).filter((s) => s.label);
      if (!src.length) body.append(txt("p", "apx-empty", "Nguồn sẽ hiện ở đây sau khi bạn hỏi."));
      src.forEach((s, i) => {
        const card = el("div", "apx-srccard");
        const head = el("div", "apx-srchead");
        head.append(txt("span", "apx-srcnum", String(i + 1)), txt("span", "apx-srclabel", s.label));
        card.append(head);
        const meta = [s.scope, s.at].filter(Boolean).join(" · ");
        if (meta) card.append(txt("div", "apx-srcmeta", meta));
        if (s.note) card.append(txt("div", "apx-srcnote", s.note));
        if (s.url && /^https?:\/\//i.test(s.url)) {
          const a = el("a", "apx-a", { href: s.url, target: "_blank", rel: "noopener noreferrer nofollow" });
          a.textContent = s.url.replace(/^https?:\/\//, "").slice(0, 60);
          card.append(a);
        }
        body.append(card);
      });
      body.append(txt("p", "apx-aside-foot", "Chỉ dữ liệu vai của bạn được xem mới vào câu trả lời."));
    } else if (this.asideTab === "activity") {
      body.append(txt("p", "apx-aside-hint", "Agent đã làm gì trong hội thoại này — để bạn kiểm tra."));
      const turns = msgs.filter((m) => m.role === "assistant" && ((m.trace && m.trace.length) || (m.proposals && m.proposals.length)));
      if (!turns.length) body.append(txt("p", "apx-empty", "Các bước tra dữ liệu, tìm internet, đề xuất hành động sẽ hiện ở đây."));
      for (const m of turns.slice(-6).reverse()) {
        const i = msgs.indexOf(m);
        const q = i > 0 ? msgs[i - 1] : undefined;
        const block = el("div", "apx-act-block");
        block.append(txt("button", "apx-act-q", q && q.role === "user" ? q.text.slice(0, 90) : "Câu trả lời"));
        (block.firstChild as HTMLElement).setAttribute("data-focus", m.id);
        const ol = el("ol", "apx-steps");
        for (const s of m.trace || []) {
          const li = el("li", "apx-step" + (/internet/i.test(s.label) ? " apx-k-web" : /đề xuất/i.test(s.label) ? " apx-k-write" : ""));
          li.append(txt("span", "apx-step-t", "+" + (s.t / 1000).toFixed(1).replace(".", ",") + "s"), txt("span", "", s.label));
          ol.append(li);
        }
        for (const p of m.proposals || []) {
          const st = stateLabel(p);
          const li = el("li", "apx-step apx-k-write");
          li.append(txt("span", "apx-step-t", "✎"), txt("span", "", p.title + " — " + st.text));
          ol.append(li);
        }
        block.append(ol);
        body.append(block);
      }
    } else if (this.asideTab === "context") {
      this.renderContext(body);
    } else {
      body.append(txt("p", "apx-aside-hint", "Biểu đồ, bảng, sơ đồ và file agent tạo trong hội thoại."));
      const arts = artifactsOf(msgs);
      if (!arts.length) body.append(txt("p", "apx-empty", "Chưa có. Thử hỏi \"Vẽ biểu đồ sản lượng theo ngày tháng 10\"."));
      const ICON = { chart: "chart", html: "file", table: "file", pdf: "download" } as const;
      for (const a of arts) {
        const row = el(a.url ? "a" : "button", "apx-artrow", a.url ? { href: a.url, target: "_blank", rel: "noopener" } : { type: "button", "data-focus": a.messageId });
        const ic = el("span", "apx-artico");
        ic.innerHTML = icon(ICON[a.kind], 14);
        row.append(ic, txt("span", "apx-arttitle", a.title));
        body.append(row);
      }
      if (last) body.append(btn("apx-btn apx-btn-ghost apx-sm apx-block", "Lưu câu trả lời gần nhất thành báo cáo", "save", { "data-save": last.id }));
    }
  }

  private onAsideClick(e: Event): void {
    const t = e.target as HTMLElement;
    const tab = (t.closest("[data-aside]") as HTMLElement | null)?.dataset.aside as AsideTab | undefined;
    if (tab) {
      this.asideTab = tab;
      return this.renderAside();
    }
    const focus = (t.closest("[data-focus]") as HTMLElement | null)?.dataset.focus;
    if (focus) return this.host.view.focusMessage(focus);
    const save = (t.closest("[data-save]") as HTMLElement | null)?.dataset.save;
    if (save) return void this.host.view.refs.thread.querySelector<HTMLElement>('[data-id="' + save + '"] [data-tool="save"]')?.click();
    const mem = (t.closest("[data-mem]") as HTMLElement | null)?.dataset.mem;
    if (mem) void this.memoryAction(mem, (t.closest("[data-mem-id]") as HTMLElement | null)?.dataset.memId);
  }

  /* ------------------------------------------------------------ Duyệt */
  private async renderApprovals(): Promise<void> {
    this.view.replaceChildren(txt("p", "apx-loading", "Đang tải…"));
    let data: { canApprove: boolean; pending: ActionProposal[]; decided: ActionProposal[]; mine: ActionProposal[] };
    try {
      const raw = await this.host.api<Record<string, unknown>>("GET", "/approvals");
      data = { canApprove: raw.canApprove === true, pending: normalizeProposals(raw.pending, 100), decided: normalizeProposals(raw.decided, 100), mine: normalizeProposals(raw.mine, 100) };
    } catch (err) {
      this.view.replaceChildren(txt("p", "apx-empty", "Không tải được: " + (err as Error).message));
      return;
    }
    if (!data.canApprove && this.approvalsFilter !== "mine") this.approvalsFilter = "mine";
    const lists: Record<string, ActionProposal[]> = { pending: data.pending, mine: data.mine, decided: data.decided };
    const list = lists[this.approvalsFilter] || [];
    const wrap = el("div", "apx-md");
    const left = el("div", "apx-md-list");
    const seg = el("div", "apx-seg", { role: "tablist" });
    const filters: Array<[ApprovalFilter, string]> = data.canApprove
      ? [["pending", "Chờ tôi duyệt (" + data.pending.length + ")"], ["mine", "Tôi đề xuất (" + data.mine.length + ")"], ["decided", "Đã xử lý"]]
      : [["mine", "Đề xuất của tôi (" + data.mine.length + ")"]];
    for (const [k, label] of filters) seg.append(btn("apx-seg-btn" + (k === this.approvalsFilter ? " apx-on" : ""), label, undefined, { "data-filter": k }));
    left.append(txt("h3", "apx-md-title", data.canApprove ? "Hộp duyệt" : "Đề xuất của tôi"), seg);
    if (!list.length) {
      left.append(txt("p", "apx-empty", this.approvalsFilter === "pending" ? "Không có đề xuất nào chờ bạn duyệt." : "Chưa có đề xuất. Trong chat, nhờ AI \"Tạo đề nghị mua bao VAJ-98 cho đơn 01\" để thử."));
    }
    if (!list.some((p) => p.id === this.selected.approvals)) this.selected.approvals = list[0]?.id || null;
    for (const p of list) {
      const st = stateLabel(p);
      const row = el("button", "apx-item" + (p.id === this.selected.approvals ? " apx-on" : ""), { type: "button", "data-pick": p.id });
      const top = el("span", "apx-item-top");
      top.append(txt("span", "apx-badge apx-pri-" + (p.priority || "normal"), p.priority === "high" ? "Ưu tiên cao" : "Thường"), txt("span", "apx-item-time", p.deadline && p.state === "awaiting_approval" ? "hạn " + clock(p.deadline) : ""));
      row.append(top, txt("span", "apx-item-title", p.title), txt("span", "apx-item-sub", (p.requester || "") + " · " + p.label), txt("span", "apx-item-state apx-tone-" + st.tone, st.text));
      left.append(row);
    }
    const right = el("div", "apx-md-detail");
    const sel = list.find((p) => p.id === this.selected.approvals);
    if (sel) {
      right.append(renderProposal(sel, "inbox"));
      if (this.approvalsFilter === "pending" && sel.state === "awaiting_approval") {
        const box = el("div", "apx-decide");
        const note = el("textarea", "apx-note", { rows: "2", placeholder: "Ghi chú cho người đề xuất (bắt buộc khi yêu cầu bổ sung)…", "aria-label": "Ghi chú" });
        const row = el("div", "apx-prop-buttons");
        row.append(
          btn("apx-btn apx-btn-ghost", "Yêu cầu bổ sung", "info", { "data-decide": "info" }),
          btn("apx-btn apx-btn-danger", "Từ chối", "close", { "data-decide": "reject" }),
          btn("apx-btn apx-btn-ok", "Duyệt", "check", { "data-decide": "approve" }),
        );
        box.append(note, row, txt("p", "apx-aside-foot", "Bản demo: duyệt sẽ tạo số chứng từ giả, chưa ghi vào ERP thật. Người đề xuất nhận thông báo."));
        right.append(box);
      }
      const erpId = this.approvalsFilter === "mine" ? reconnectTarget(sel as Parameters<typeof reconnectTarget>[0], this.erpLinks) : null;
      if (erpId) {
        /* 13.6 AC2: dừng vì token ERP hết hạn / chưa kết nối ⇒ "Kết nối lại" đi đúng luồng 13.5, rồi gửi lại đề xuất */
        const box = el("div", "apx-decide");
        box.append(txt("p", "apx-aside-hint", "Đề xuất dừng vì tài khoản ERP của bạn chưa kết nối hoặc đã hết hạn — chưa ghi gì vào ERP. Kết nối lại rồi đề xuất lại trong hội thoại."),
          btn("apx-btn apx-btn-primary", "Kết nối lại tài khoản ERP", "link", { "data-erp-act": "reconnect", "data-erp-id": erpId }));
        right.append(box);
      } else if (this.approvalsFilter === "mine" && sel.state === "need_info") {
        right.append(txt("p", "apx-aside-hint", "Mở lại hội thoại gốc, trả lời người duyệt rồi bấm \"Gửi lại để duyệt\" trên thẻ."));
      }
    } else {
      right.append(txt("p", "apx-empty", "Chọn một đề xuất để xem chi tiết."));
    }
    wrap.append(left, right);
    this.view.replaceChildren(wrap);
  }

  /* ------------------------------------------------------------ Báo cáo */
  private async renderReports(): Promise<void> {
    let jobs: Job[] = [];
    try {
      jobs = (await this.host.api<{ jobs: Job[] }>("GET", "/jobs")).jobs || [];
    } catch (err) {
      this.view.replaceChildren(txt("p", "apx-empty", "Không tải được: " + (err as Error).message));
      return;
    }
    if (this.selected.reports !== "new" && !jobs.some((j) => j.id === this.selected.reports)) this.selected.reports = jobs[0]?.id || "new";
    const wrap = el("div", "apx-md");
    const left = el("div", "apx-md-list");
    const head = el("div", "apx-md-head");
    head.append(txt("h3", "apx-md-title", "Báo cáo đã lưu"), btn("apx-btn apx-btn-primary apx-sm", "Mới", "plus", { "data-pick-job": "new" }));
    left.append(head, txt("p", "apx-aside-hint", "Lưu một câu trả lời (nút 💾 dưới câu trả lời) hoặc tạo mới; đặt lịch để AI tự chạy lại và báo bạn."));
    for (const j of jobs) {
      const row = el("button", "apx-item" + (j.id === this.selected.reports ? " apx-on" : ""), { type: "button", "data-pick-job": j.id });
      const top = el("span", "apx-item-top");
      top.append(txt("span", "apx-badge" + (j.paused ? " apx-pri-paused" : j.schedule.kind === "none" ? "" : " apx-pri-sched"), j.paused ? "Tạm dừng" : j.scheduleLabel), txt("span", "apx-item-time", j.lastRun ? ago(j.lastRun.at) : ""));
      row.append(top, txt("span", "apx-item-title", j.title));
      if (j.lastRun) row.append(txt("span", "apx-item-state apx-tone-" + (j.lastRun.status === "ok" ? "ok" : j.lastRun.status === "error" ? "bad" : "info"), j.lastRun.status === "ok" ? j.runCount + " phiên bản" : j.lastRun.status === "error" ? "Lần chạy cuối lỗi" : "Đang chạy…"));
      left.append(row);
    }
    const right = el("div", "apx-md-detail");
    if (this.selected.reports === "new") right.append(this.jobForm());
    else if (this.selected.reports) await this.jobDetail(right, this.selected.reports);
    wrap.append(left, right);
    this.view.replaceChildren(wrap);
  }

  private scheduleControls(s: Job["schedule"]): HTMLElement {
    const box = el("div", "apx-sched");
    const kind = el("select", "apx-select", { "data-sched": "kind", "aria-label": "Lịch chạy" });
    for (const [v, l] of [["none", "Thủ công"], ["daily", "Hằng ngày"], ["weekly", "Hằng tuần"], ["hourly", "Mỗi giờ"]] as const) {
      const o = el("option", "", { value: v });
      o.textContent = l;
      if (s.kind === v) o.selected = true;
      kind.append(o);
    }
    box.append(kind);
    if (s.kind === "weekly") {
      const dow = el("select", "apx-select", { "data-sched": "dow", "aria-label": "Thứ" });
      ["Chủ nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"].forEach((l, i) => {
        const o = el("option", "", { value: String(i) });
        o.textContent = l;
        if ((s.dow ?? 1) === i) o.selected = true;
        dow.append(o);
      });
      box.append(dow);
    }
    if (s.kind === "daily" || s.kind === "weekly") box.append(el("input", "apx-select", { type: "time", value: s.time || "07:00", "data-sched": "time", "aria-label": "Giờ chạy" }));
    return box;
  }

  private readSchedule(scope: HTMLElement): Job["schedule"] {
    const kind = scope.querySelector<HTMLSelectElement>('[data-sched="kind"]')?.value || "none";
    const time = scope.querySelector<HTMLInputElement>('[data-sched="time"]')?.value || "07:00";
    const dow = Number(scope.querySelector<HTMLSelectElement>('[data-sched="dow"]')?.value ?? 1);
    return { kind, time, dow };
  }

  private jobForm(): HTMLElement {
    const form = el("form", "apx-jobform");
    form.append(txt("h3", "apx-md-title", "Tạo báo cáo mới"));
    const title = el("input", "apx-field", { name: "title", placeholder: "Tên báo cáo, ví dụ: Sản lượng hằng ngày", "aria-label": "Tên báo cáo", maxlength: "120" });
    const prompt = el("textarea", "apx-field", { name: "prompt", rows: "4", placeholder: "Câu hỏi để AI chạy lại mỗi lần, ví dụ: Vẽ biểu đồ sản lượng theo ngày tháng 10 và nhận xét ngắn", "aria-label": "Câu hỏi", maxlength: "1000" });
    form.append(txt("label", "apx-lbl", "Tên"), title, txt("label", "apx-lbl", "Câu hỏi"), prompt, txt("label", "apx-lbl", "Lịch chạy"), this.scheduleControls({ kind: "daily", time: "07:00" }));
    const row = el("div", "apx-prop-buttons");
    row.append(btn("apx-btn apx-btn-primary", "Lưu & chạy ngay", "play", { "data-job": "create" }));
    form.append(row, txt("p", "apx-aside-hint", "Báo cáo chạy bằng chính quyền của bạn — chỉ thấy dữ liệu vai của bạn được xem. Lỗi 3 lần liên tiếp thì lịch tự tạm dừng và báo bạn."));
    form.addEventListener("submit", (e) => e.preventDefault());
    return form;
  }

  private async jobDetail(right: HTMLElement, jid: string): Promise<void> {
    let job: Job;
    try {
      job = (await this.host.api<{ job: Job }>("GET", "/jobs/" + jid)).job;
    } catch (err) {
      right.append(txt("p", "apx-empty", "Không tải được: " + (err as Error).message));
      return;
    }
    const runs = job.runs || [];
    const run = runs.find((r) => r.id === this.selected.run) || runs[0];
    const head = el("div", "apx-jobhead");
    head.append(txt("h3", "apx-md-title", job.title), txt("p", "apx-jobprompt", "Câu hỏi: " + job.prompt));
    const ctl = el("div", "apx-jobctl", { "data-job-id": job.id });
    ctl.append(this.scheduleControls(job.schedule));
    ctl.append(
      btn("apx-btn apx-btn-primary apx-sm", "Chạy ngay", "play", { "data-job": "run" }),
      btn("apx-btn apx-btn-ghost apx-sm", job.paused ? "Tiếp tục lịch" : "Tạm dừng", job.paused ? "play" : "pause", { "data-job": job.paused ? "resume" : "pause" }),
    );
    if (run && run.status === "ok") ctl.append(btn("apx-btn apx-btn-ghost apx-sm", "PDF", "download", { "data-job": "pdf", "data-run": run.id }));
    ctl.append(btn("apx-btn apx-btn-ghost apx-sm apx-danger", "", "trash", { "data-job": "delete", title: "Xoá báo cáo", "aria-label": "Xoá báo cáo" }));
    head.append(ctl);
    const next = job.paused ? "Lịch đang tạm dừng" : job.nextAt ? "Lần chạy tới: " + clock(job.nextAt) : "Chạy thủ công";
    head.append(txt("p", "apx-aside-hint", job.scheduleLabel + " · " + next + (job.deliver?.to?.length ? " · gửi email tới " + job.deliver.to.join(", ") + " (kèm PDF)" : "")));
    right.append(head);

    if (runs.length) {
      const vers = el("select", "apx-select apx-versions", { "data-run-pick": "1", "aria-label": "Phiên bản" });
      runs.forEach((r, i) => {
        const o = el("option", "", { value: r.id });
        o.textContent = "Phiên bản " + (runs.length - i) + " · " + clock(r.at) + " · " + (r.status === "ok" ? "OK" : r.status === "error" ? "Lỗi" : "Đang chạy") + (r.by === "schedule" ? " · theo lịch" : r.by === "manual" ? " · thủ công" : r.by ? " · " + r.by : "");
        if (run && r.id === run.id) o.selected = true;
        vers.append(o);
      });
      right.append(vers);
    }
    const doc = el("div", "apx-msg apx-msg-assistant apx-report");
    if (!run) doc.append(txt("p", "apx-empty", "Chưa có phiên bản nào — bấm Chạy ngay."));
    else if (run.status === "running") doc.append(txt("p", "apx-loading", "AI đang chạy báo cáo… (thường 10–60 giây)"));
    else if (run.status === "error") doc.append(txt("p", "apx-error-line", "Lần chạy lỗi: " + (run.error || "không rõ")));
    else {
      const body = el("div", "apx-body");
      body.innerHTML = renderMarkdown(run.text || "");
      doc.append(body);
      const src = (run.sources || []).filter((s) => s.label);
      if (src.length) {
        const sb = el("div", "apx-sources");
        for (const s of src) sb.append(txt("p", "apx-source", "Nguồn: " + [s.label, s.at, s.scope].filter(Boolean).join(" · ")));
        doc.append(sb);
      }
    }
    right.append(doc);
    if (run && run.trace && run.trace.length) {
      const det = el("details", "apx-prop-history");
      det.append(txt("summary", "", "Chi tiết lần chạy (" + run.trace.length + " bước · " + (run.ms / 1000).toFixed(1).replace(".", ",") + " giây)"));
      const ol = el("ol", "apx-steps");
      for (const s of run.trace) {
        const li = el("li", "apx-step");
        li.append(txt("span", "apx-step-t", "+" + (s.t / 1000).toFixed(1).replace(".", ",") + "s"), txt("span", "", s.label));
        ol.append(li);
      }
      det.append(ol);
      right.append(det);
    }
    /* đang chạy: hỏi lại sau 3 giây cho tới khi xong */
    if (this.jobPoll) clearTimeout(this.jobPoll);
    if (runs.some((r) => r.status === "running")) {
      this.jobPoll = setTimeout(() => {
        if (this.active && this.tab === "reports" && this.selected.reports === jid) void this.renderReports();
      }, 3000);
    }
  }

  /* ----------------------------------------------------------- Quản trị */
  private async renderAdmin(): Promise<void> {
    this.view.replaceChildren(txt("p", "apx-loading", "Đang tổng hợp chi phí và mức sử dụng…"));
    try {
      const r = await this.host.api<{ markdown: string; generatedAt: number; costUsd: number; billing: { totalUsd: number } | null }>("GET", "/admin?days=" + this.adminDays);
      const wrap = el("div", "apx-overview");
      const head = el("div", "apx-md-head");
      const sel = el("select", "apx-select apx-versions", { "data-admin-days": "1", "aria-label": "Khoảng thời gian" });
      for (const d of [7, 30, 90]) {
        const o = el("option", "", { value: String(d) });
        o.textContent = d + " ngày";
        if (d === this.adminDays) o.selected = true;
        sel.append(o);
      }
      const tools = el("div", "apx-jobctl");
      tools.append(sel, btn("apx-btn apx-btn-ghost apx-sm", "Làm mới", "refresh", { "data-admin": "refresh" }));
      head.append(txt("h3", "apx-md-title", "Quản trị · chi phí AI và mức sử dụng"), tools);
      const kpi = el("div", "apx-kpis");
      const card = (label: string, value: string, sub: string) => {
        const c = el("div", "apx-kpi");
        c.append(txt("span", "apx-kpi-l", label), txt("strong", "apx-kpi-v", value), txt("span", "apx-kpi-s", sub));
        kpi.append(c);
      };
      card("Chi phí ước tính", "$" + r.costUsd.toFixed(r.costUsd < 1 ? 4 : 2), this.adminDays + " ngày · token × đơn giá");
      card("Hoá đơn OpenAI", r.billing ? "$" + r.billing.totalUsd.toFixed(2) : "—", r.billing ? "Costs API · cả tổ chức" : "chưa có OPENAI_ADMIN_KEY");
      const doc = el("div", "apx-msg apx-msg-assistant apx-report");
      const body = el("div", "apx-body");
      body.innerHTML = renderMarkdown(r.markdown || "");
      doc.append(body);
      wrap.append(head, txt("p", "apx-aside-hint", "Số thật từ nhật ký của server · cập nhật " + clock(r.generatedAt) + ". Nội dung câu hỏi không hiển thị ở đây, trừ các câu bị đánh giá 👎 để rà chất lượng."), kpi, doc);
      this.view.replaceChildren(wrap);
    } catch (err) {
      this.view.replaceChildren(txt("p", "apx-empty", "Không tải được: " + (err as Error).message));
    }
  }

  /* ----------------------------------------------------------- Tổng quan */
  private async renderOverview(): Promise<void> {
    this.view.replaceChildren(txt("p", "apx-loading", "Đang tổng hợp…"));
    try {
      const r = await this.host.api<{ markdown: string; generatedAt: number; roles?: Array<{ id: string; label: string }> }>("GET", "/overview" + (this.overviewRole ? "?role=" + encodeURIComponent(this.overviewRole) : ""));
      const wrap = el("div", "apx-overview");
      const head = el("div", "apx-md-head");
      const sel = el("select", "apx-select apx-versions", { "data-ov-role": "1", "aria-label": "Phạm vi" });
      const opts: Array<[string, string]> = [["", "Toàn công ty"], ...(r.roles || []).map((x): [string, string] => [x.id, "Phòng/vai: " + x.label])];
      for (const [v, l] of opts) {
        const o = el("option", "", { value: v });
        o.textContent = l;
        if (v === this.overviewRole) o.selected = true;
        sel.append(o);
      }
      const tools = el("div", "apx-jobctl");
      tools.append(sel, btn("apx-btn apx-btn-ghost apx-sm", "Làm mới", "refresh", { "data-ov": "refresh" }));
      head.append(txt("h3", "apx-md-title", "Tổng quan sử dụng Apus Ami"), tools);
      const doc = el("div", "apx-msg apx-msg-assistant apx-report");
      const body = el("div", "apx-body");
      body.innerHTML = renderMarkdown(r.markdown || "");
      doc.append(body);
      wrap.append(head, txt("p", "apx-aside-hint", "Số liệu thật từ nhật ký của server demo (7 ngày) · cập nhật " + clock(r.generatedAt)), doc);
      this.view.replaceChildren(wrap);
    } catch (err) {
      this.view.replaceChildren(txt("p", "apx-empty", "Không tải được: " + (err as Error).message));
    }
  }

  /* ---------------------------------------------- sự kiện vùng tab khác */
  private async onViewClick(e: Event): Promise<void> {
    const t = e.target as HTMLElement;
    const attr = (k: string) => (t.closest("[data-" + k + "]") as HTMLElement | null)?.getAttribute("data-" + k) || null;
    if (this.onErpAct(t)) return;
    try {
      if (this.tab === "tasks" && (await this.onTaskClick(t))) return;
      const filter = attr("filter");
      if (filter) {
        this.approvalsFilter = filter as ApprovalFilter;
        this.selected.approvals = null;
        return void this.renderApprovals();
      }
      const pick = attr("pick");
      if (pick) {
        this.selected.approvals = pick;
        return void this.renderApprovals();
      }
      const decision = attr("decide");
      if (decision && this.selected.approvals) {
        const note = this.view.querySelector<HTMLTextAreaElement>(".apx-note")?.value || "";
        (t.closest("button") as HTMLButtonElement).disabled = true;
        await this.host.api("POST", "/approvals/" + this.selected.approvals + "/decide", { decision, note });
        this.toast(decision === "approve" ? "Đã duyệt — người đề xuất đã được báo" : decision === "reject" ? "Đã từ chối" : "Đã gửi yêu cầu bổ sung");
        await this.refreshMe();
        return void this.renderApprovals();
      }
      const pickJob = attr("pick-job");
      if (pickJob) {
        this.selected.reports = pickJob;
        this.selected.run = null;
        return void this.renderReports();
      }
      const job = attr("job");
      if (job === "create") {
        const form = t.closest("form") as HTMLFormElement;
        const title = (form.querySelector('[name="title"]') as HTMLInputElement).value;
        const prompt = (form.querySelector('[name="prompt"]') as HTMLTextAreaElement).value;
        if (!prompt.trim()) return this.toast("Cần nhập câu hỏi");
        const r = await this.host.api<{ job: Job }>("POST", "/jobs", { title, prompt, schedule: this.readSchedule(form) });
        await this.host.api("POST", "/jobs/" + r.job.id + "/run");
        this.selected.reports = r.job.id;
        this.toast("Đã lưu — AI đang chạy lần đầu");
        return void this.renderReports();
      }
      const jid = this.selected.reports;
      if (job && jid && jid !== "new") {
        if (job === "run") {
          await this.host.api("POST", "/jobs/" + jid + "/run");
          this.selected.run = null;
          this.toast("Đang chạy lại báo cáo…");
        } else if (job === "pause" || job === "resume") {
          await this.host.api("PATCH", "/jobs/" + jid, { paused: job === "pause" });
        } else if (job === "delete") {
          await this.host.api("DELETE", "/jobs/" + jid);
          this.selected.reports = null;
          this.toast("Đã xoá báo cáo");
        } else if (job === "pdf") {
          const r = await this.host.api<{ url: string }>("POST", "/jobs/" + jid + "/runs/" + attr("run") + "/export");
          await this.host.previewPdf(r.url, "Báo cáo Apus Ami.pdf");
          return;
        }
        return void this.renderReports();
      }
      if (attr("ov") === "refresh") return void this.renderOverview();
      if (attr("admin") === "refresh") return void this.renderAdmin();
    } catch (err) {
      this.toast("Không thực hiện được: " + (err as Error).message);
      if (this.tab === "approvals") void this.renderApprovals();
    }
  }

  private async onViewChange(e: Event): Promise<void> {
    const t = e.target as HTMLElement;
    if (t.matches("[data-admin-days]")) {
      this.adminDays = Number((t as HTMLSelectElement).value) || 30;
      return void this.renderAdmin();
    }
    if (t.matches("[data-ov-role]")) {
      this.overviewRole = (t as HTMLSelectElement).value;
      return void this.renderOverview();
    }
    if (t.matches("[data-run-pick]")) {
      this.selected.run = (t as HTMLSelectElement).value;
      return void this.renderReports();
    }
    if (t.matches("[data-sched]")) {
      const ctl = t.closest("[data-job-id]") as HTMLElement | null;
      if (!ctl) {
        /* form tạo mới: vẽ lại ô giờ/thứ theo loại lịch */
        const form = t.closest("form");
        const box = form?.querySelector(".apx-sched");
        if (box && t.getAttribute("data-sched") === "kind") box.replaceWith(this.scheduleControls(this.readSchedule(form as HTMLElement)));
        return;
      }
      try {
        await this.host.api("PATCH", "/jobs/" + ctl.dataset.jobId, { schedule: this.readSchedule(ctl) });
        this.toast("Đã cập nhật lịch");
      } catch (err) {
        this.toast("Không cập nhật được: " + (err as Error).message);
      }
      void this.renderReports();
    }
  }

  /* ------------------------------------------------ chia sẻ hội thoại */
  private async onShare(act: string, cid: string, sid?: string): Promise<void> {
    try {
      if (act === "close") return void this.convhead.querySelector(".apx-sharepop")?.remove();
      if (act === "create7" || act === "create1" || act === "create30") {
        const r = await this.host.api<{ url: string; expiresAt: number }>("POST", "/conversations/" + cid + "/shares", { days: Number(act.slice(6)) });
        try {
          await navigator.clipboard.writeText(r.url);
          this.toast("Đã tạo và sao chép link — hết hạn " + clock(r.expiresAt));
        } catch {
          this.toast("Đã tạo link — hết hạn " + clock(r.expiresAt));
        }
      } else if (act === "revoke" && sid) {
        await this.host.api("DELETE", "/shares/" + sid);
        this.toast("Đã thu hồi link");
      } else if (act === "copy" && sid) {
        const url = this.convhead.querySelector<HTMLAnchorElement>('[data-sid="' + sid + '"] a')?.href || "";
        await navigator.clipboard.writeText(url).catch(() => undefined);
        return this.toast("Đã sao chép link");
      }
      const list = (await this.host.api<{ shares: Array<{ id: string; url: string; expiresAt: number; views: number }> }>("GET", "/conversations/" + cid + "/shares")).shares || [];
      this.convhead.querySelector(".apx-sharepop")?.remove();
      const pop = el("div", "apx-sharepop", { role: "dialog", "aria-label": "Chia sẻ hội thoại" });
      const head = el("div", "apx-bellhead");
      head.append(txt("strong", "", "Chia sẻ chỉ đọc"), btn("apx-link", "Đóng", undefined, { "data-share": "close" }));
      pop.append(head, txt("p", "apx-aside-hint", "Người có link xem được bản chụp hội thoại TẠI LÚC TẠO (tin nhắn sau đó không lộ), không cần đăng nhập. Thu hồi bất cứ lúc nào."));
      const row = el("div", "apx-prop-buttons");
      row.append(
        btn("apx-btn apx-btn-ghost apx-sm", "1 ngày", undefined, { "data-share": "create1" }),
        btn("apx-btn apx-btn-primary apx-sm", "Tạo link 7 ngày", "link", { "data-share": "create7" }),
        btn("apx-btn apx-btn-ghost apx-sm", "30 ngày", undefined, { "data-share": "create30" }),
      );
      pop.append(row);
      for (const x of list) {
        const it = el("div", "apx-shareitem", { "data-sid": x.id });
        const a = el("a", "apx-a", { href: x.url, target: "_blank", rel: "noopener" });
        a.textContent = "Link · hết hạn " + clock(x.expiresAt) + " · " + x.views + " lượt xem";
        it.append(a, btn("apx-link", "Sao chép", undefined, { "data-share": "copy" }), btn("apx-link apx-danger", "Thu hồi", undefined, { "data-share": "revoke" }));
        pop.append(it);
      }
      this.convhead.append(pop);
    } catch (err) {
      this.toast("Không chia sẻ được: " + (err as Error).message);
    }
  }

  /* ------------------------------------------- cột phải: ngữ cảnh + ghi nhớ */
  private renderContext(body: HTMLElement): void {
    const me = this.me;
    const ctx = this.host.context() || {};
    const screen = typeof ctx.screen === "string" ? ctx.screen : typeof ctx.board === "string" ? ctx.board : "";
    const box = el("div", "apx-srccard");
    box.append(txt("div", "apx-srclabel", "Bạn đang ở"));
    box.append(txt("div", "apx-srcmeta", screen ? "Màn hình ERP: " + screen + (ctx.no ? " · đơn " + String(ctx.no) : "") : "Chưa có ngữ cảnh màn hình (ERP gửi qua assistant.open({ screen }))"));
    box.append(txt("div", "apx-srcmeta", "Vai: " + (me?.roleLabel || "…") + (me?.snapshot ? " · dữ liệu ngày " + me.snapshot : "")));
    if (me?.can?.length) box.append(txt("div", "apx-srcnote", "Được xem: " + me.can.join(", ")));
    body.append(box);

    const mem = me?.memory || { enabled: true, items: [] };
    const head = el("div", "apx-memhead");
    head.append(
      txt("strong", "", "Apus Ami ghi nhớ về bạn"),
      btn("apx-switch" + (mem.enabled ? " apx-on" : ""), mem.enabled ? "Đang bật" : "Đang tắt", undefined, { "data-mem": "toggle", role: "switch", "aria-checked": String(mem.enabled) }),
    );
    body.append(head, txt("p", "apx-aside-hint", "Sở thích, thói quen làm việc bạn nói với Apus Ami — để trả lời hợp ý hơn. Không lưu số liệu ERP."));
    if (!mem.items.length) body.append(txt("p", "apx-empty", 'Chưa ghi nhớ gì. Thử nói "Từ giờ báo số theo tấn, làm tròn 1 chữ số".'));
    for (const m of mem.items) {
      const row = el("div", "apx-memrow", { "data-mem-id": m.id });
      row.append(
        txt("span", "apx-memtext", m.text),
        txt("span", "apx-conv-meta", (m.source === "user" ? "bạn thêm" : "AI ghi") + " · " + ago(m.at)),
        btn("apx-mini", "", "trash", { "data-mem": "forget", title: "Quên mục này", "aria-label": "Quên mục này" }),
      );
      body.append(row);
    }
    const add = el("div", "apx-memadd");
    add.append(el("input", "apx-search apx-memin", { placeholder: "Thêm ghi nhớ…", "aria-label": "Thêm ghi nhớ", maxlength: "200" }), btn("apx-btn apx-btn-ghost apx-sm", "Thêm", "plus", { "data-mem": "add" }));
    body.append(add);
    if (mem.items.length) body.append(btn("apx-link apx-danger", "Xoá toàn bộ ghi nhớ", undefined, { "data-mem": "clear" }));
  }

  private async memoryAction(act: string, mid?: string): Promise<void> {
    try {
      if (act === "toggle") await this.host.api("PATCH", "/memory", { enabled: !(this.me?.memory?.enabled ?? true) });
      else if (act === "forget" && mid) await this.host.api("DELETE", "/memory/" + mid);
      else if (act === "clear") await this.host.api("DELETE", "/memory");
      else if (act === "add") {
        const v = this.aside.querySelector<HTMLInputElement>(".apx-memin")?.value.trim();
        if (!v) return;
        await this.host.api("POST", "/memory", { text: v });
      }
      await this.refreshMe();
      this.renderAside();
    } catch (err) {
      this.toast("Không thực hiện được: " + (err as Error).message);
    }
  }

  /* ------------------------------------------------------------ Giao việc */
  private async renderTasks(): Promise<void> {
    let tasks: Task[] = [];
    try {
      tasks = (await this.host.api<{ tasks: Task[] }>("GET", "/tasks")).tasks || [];
    } catch (err) {
      this.view.replaceChildren(txt("p", "apx-empty", "Không tải được: " + (err as Error).message));
      return;
    }
    if (this.selected.tasks !== "new" && !tasks.some((x) => x.id === this.selected.tasks)) this.selected.tasks = tasks[0]?.id || "new";
    const wrap = el("div", "apx-md");
    const left = el("div", "apx-md-list");
    const head = el("div", "apx-md-head");
    head.append(txt("h3", "apx-md-title", "Giao việc cho Apus Ami"), btn("apx-btn apx-btn-primary apx-sm", "Việc mới", "plus", { "data-task-pick": "new" }));
    left.append(head, txt("p", "apx-aside-hint", "Giao một mục tiêu nhiều bước; Apus Ami tự lập kế hoạch, tra dữ liệu, soạn đề xuất và báo khi xong hoặc khi cần bạn quyết định. Bạn không phải ngồi chờ."));
    for (const x of tasks) {
      const [label, tone] = TASK_STATE[x.status] || ["?", "muted"];
      const row = el("button", "apx-item" + (x.id === this.selected.tasks ? " apx-on" : ""), { type: "button", "data-task-pick": x.id });
      const top = el("span", "apx-item-top");
      top.append(txt("span", "apx-item-state apx-tone-" + tone, label), txt("span", "apx-item-time", ago(x.updatedAt)));
      row.append(top, txt("span", "apx-item-title", x.title), txt("span", "apx-item-sub", x.steps.length + " bước" + (x.proposals.length ? " · " + x.proposals.length + " đề xuất" : "")));
      left.append(row);
    }
    const right = el("div", "apx-md-detail");
    if (this.selected.tasks === "new" || !this.selected.tasks) right.append(this.taskForm());
    else {
      const sel = this.selected.tasks;
      try {
        const t = (await this.host.api<{ task: Task }>("GET", "/tasks/" + sel)).task;
        this.taskDetail(right, t);
        if (this.taskPoll) clearTimeout(this.taskPoll);
        if (t.status === "running") {
          this.taskPoll = setTimeout(() => {
            if (this.active && this.tab === "tasks" && this.selected.tasks === sel) void this.renderTasks();
          }, 3000);
        }
      } catch (err) {
        right.append(txt("p", "apx-empty", "Không tải được: " + (err as Error).message));
      }
    }
    wrap.append(left, right);
    this.view.replaceChildren(wrap);
  }

  private taskForm(): HTMLElement {
    const form = el("form", "apx-jobform");
    form.addEventListener("submit", (e) => e.preventDefault());
    form.append(txt("h3", "apx-md-title", "Việc mới"), txt("label", "apx-lbl", "Mục tiêu (càng cụ thể càng tốt)"));
    const goal = el("textarea", "apx-field", { name: "goal", rows: "4", maxlength: "1000", "aria-label": "Mục tiêu", placeholder: "Ví dụ: Kiểm tra tồn vật tư cho KHSX tuần 43 và lập đề nghị mua cho phần thiếu" });
    form.append(goal, txt("label", "apx-lbl", "Gợi ý"));
    const ideas = el("div", "apx-ideas");
    for (const i of TASK_IDEAS) ideas.append(btn("apx-chip", i, undefined, { "data-task-idea": i }));
    form.append(ideas);
    const row = el("div", "apx-prop-buttons");
    row.append(btn("apx-btn apx-btn-primary", "Giao việc", "play", { "data-task": "create" }));
    form.append(row, txt("p", "apx-aside-hint", "Chạy bằng quyền của bạn, tối đa 3 phút mỗi lần chạy. Mọi thay đổi trên ERP vẫn chỉ là đề xuất — bạn xác nhận ở đây."));
    return form;
  }

  private taskDetail(right: HTMLElement, t: Task): void {
    const [label, tone] = TASK_STATE[t.status] || ["?", "muted"];
    const head = el("div", "apx-jobhead");
    head.append(txt("h3", "apx-md-title", t.title), txt("p", "apx-jobprompt", "Mục tiêu: " + t.goal));
    const ctl = el("div", "apx-jobctl");
    ctl.append(txt("span", "apx-item-state apx-tone-" + tone, label));
    if (t.status === "running") ctl.append(btn("apx-btn apx-btn-ghost apx-sm", "Huỷ", "stop", { "data-task": "cancel" }));
    else ctl.append(btn("apx-btn apx-btn-ghost apx-sm apx-danger", "", "trash", { "data-task": "delete", title: "Xoá việc", "aria-label": "Xoá việc" }));
    head.append(ctl);
    right.append(head);

    const tl = el("div", "apx-act-block");
    tl.append(txt("div", "apx-act-q", "Dòng thời gian (" + t.steps.length + " bước)"));
    const ol = el("ol", "apx-steps");
    for (const st of t.steps) {
      const li = el("li", "apx-step" + (/internet/i.test(st.label) ? " apx-k-web" : /đề xuất|Bạn trả lời/i.test(st.label) ? " apx-k-write" : ""));
      li.append(txt("span", "apx-step-t", clock(st.at).slice(0, 5)), txt("span", "", st.label));
      ol.append(li);
    }
    if (t.status === "running") ol.append(txt("li", "apx-step apx-k-web", "… đang làm"));
    tl.append(ol);
    right.append(tl);

    if (t.error) right.append(txt("p", "apx-error-line", "Lỗi: " + t.error));
    if (t.result) {
      const doc = el("div", "apx-msg apx-msg-assistant apx-report");
      const b = el("div", "apx-body");
      b.innerHTML = renderMarkdown(t.result);
      doc.append(b);
      right.append(doc);
    }
    const props = normalizeProposals(t.proposals, 20);
    if (props.length) {
      const holder = el("div", "apx-taskprops");
      holder.append(txt("div", "apx-act-q", "Đề xuất từ việc này — xác nhận tại đây"));
      for (const p of props) holder.append(renderProposal(p, "chat"));
      right.append(holder);
    }
    if (t.status === "needs_input" || t.status === "done" || t.status === "failed") {
      const box = el("div", "apx-decide");
      if (t.question) box.append(txt("p", "apx-prop-warn", "Apus Ami hỏi: " + t.question));
      box.append(el("textarea", "apx-note apx-taskreply", { rows: "2", maxlength: "1000", "aria-label": "Trả lời hoặc giao thêm", placeholder: t.status === "needs_input" ? "Trả lời để Apus Ami làm tiếp…" : "Giao thêm yêu cầu cho việc này…" }));
      const row = el("div", "apx-prop-buttons");
      row.append(btn("apx-btn apx-btn-primary apx-sm", "Gửi, làm tiếp", "send", { "data-task": "reply" }));
      box.append(row);
      right.append(box);
    }
  }

  private async onTaskClick(t: HTMLElement): Promise<boolean> {
    const attr = (k: string) => (t.closest("[data-" + k + "]") as HTMLElement | null)?.getAttribute("data-" + k) || null;
    const pick = attr("task-pick");
    if (pick) {
      this.selected.tasks = pick;
      void this.renderTasks();
      return true;
    }
    const idea = attr("task-idea");
    if (idea) {
      const g = this.view.querySelector<HTMLTextAreaElement>('[name="goal"]');
      if (g) g.value = idea;
      return true;
    }
    const prop = t.closest("[data-prop-do]") as HTMLElement | null;
    if (prop?.dataset.propId) {
      const card = prop.closest(".apx-prop");
      const box = card?.querySelector<HTMLTextAreaElement>(".apx-prop-override") || null;
      const note = box?.value.trim() || "";
      if (prop.dataset.propDo === "confirm" && box && !note) {
        box.focus();
        box.classList.add("apx-need");
        return true;
      }
      await this.host.api("POST", "/proposals/" + prop.dataset.propId + "/" + (prop.dataset.propDo === "confirm" ? "confirm" : "cancel"), note ? { note } : {});
      this.toast(prop.dataset.propDo === "confirm" ? "Đã xác nhận đề xuất" : "Đã huỷ đề xuất");
      void this.refreshMe();
      void this.renderTasks();
      return true;
    }
    const act = attr("task");
    if (!act) return false;
    if (act === "create") {
      const goal = this.view.querySelector<HTMLTextAreaElement>('[name="goal"]')?.value.trim() || "";
      if (!goal) {
        this.toast("Cần ghi mục tiêu");
        return true;
      }
      const r = await this.host.api<{ task: Task }>("POST", "/tasks", { goal });
      this.selected.tasks = r.task.id;
      this.toast("Đã giao việc — Apus Ami đang làm, sẽ báo bạn");
    } else if (act === "cancel" && this.selected.tasks) {
      await this.host.api("POST", "/tasks/" + this.selected.tasks + "/cancel");
    } else if (act === "delete" && this.selected.tasks) {
      await this.host.api("DELETE", "/tasks/" + this.selected.tasks);
      this.selected.tasks = null;
    } else if (act === "reply" && this.selected.tasks) {
      const text = this.view.querySelector<HTMLTextAreaElement>(".apx-taskreply")?.value.trim() || "";
      if (!text) return true;
      await this.host.api("POST", "/tasks/" + this.selected.tasks + "/reply", { text });
    }
    void this.renderTasks();
    return true;
  }
}
